import {
  PublicGetCustomerTransactionResponse,
  SharedTransactionMetadata,
} from '@bukalapak/openapi-schema/vptrader/transaction/v2/public/transaction-domain';
import VPNonTraderAPIClient from '~/api/vpnontrader';
import AgenliteAPIClient from '~/api/index';

function getMetadataValue(meta: SharedTransactionMetadata[] | undefined, name: string): string {
  return (
    meta?.find(m => {
      return m.name === name;
    })?.value || ''
  );
}

const initialState = {
  transaction: {
    vendor: {},
  },
  loading: true,
  customerNumber: '',
  educationAnnouncement: {},
  educationBanner: {},
};

const getters = {
  getEducationTransaction(state) {
    return state.transaction;
  },
  getEducationTransactionLoading(state) {
    return state.loading;
  },
  getCustomerNumber(state) {
    return state.customerNumber;
  },
  getEducationAnnouncement(state) {
    return state.educationAnnouncement;
  },
  getEducationBanner(state) {
    return state.educationBanner;
  },
};

const actions = {
  async retrieveEducationTransaction({ dispatch, commit }, payload) {
    let response: PublicGetCustomerTransactionResponse = {};

    const { options, ...requestPayload } = payload;

    try {
      if (!options?.ignoreLoading) {
        commit('setEducationTransactionLoading', true);
      }

      response = await VPNonTraderAPIClient.getCustomerTransactionEducation(requestPayload.id);

      const transaction = response.customer_transaction;
      const meta = transaction?.metadata_list;

      const isBasePriceSegmentationAdjusted =
        getMetadataValue(meta, 'is_base_price_segmentation_adjusted') === 'true';

      // Map all available metadata fields
      const customerNumber = getMetadataValue(meta, 'customer_number');

      commit('setEducationTransaction', {
        ...transaction,
        // Customer information
        customer_number: customerNumber,
        customer_name: getMetadataValue(meta, 'customer_name'),
        // Pricing information
        admin_fee: getMetadataValue(meta, 'admin_charge') || getMetadataValue(meta, 'admin_fee'),
        payment_fee: getMetadataValue(meta, 'payment_fee') || '0',
        amount: getMetadataValue(meta, 'amount'),
        // Transaction details
        bill_count: getMetadataValue(meta, 'bill_count'),
        identity: getMetadataValue(meta, 'identity'),
        product_type: getMetadataValue(meta, 'product_type'),
        reference_number: getMetadataValue(meta, 'reference_number'),
        // Flags
        is_base_price_segmentation_adjusted: isBasePriceSegmentationAdjusted,
        voucher: transaction?.voucher,
      });

      return transaction;
    } catch (error) {
      const message = 'Gagal mendapatkan data transaksi';
      dispatch('catchError', { error, message });
      throw error;
    } finally {
      commit('setEducationTransactionLoading', false);
    }
  },
  async fetchEducationAnnouncement({ commit }) {
    try {
      const { data } = await AgenliteAPIClient.fetchVirtualProductStatusInfoType();
      const announcement = data.find(
        announcement => announcement.type === 'education' && announcement.status
      );
      commit('setEducationAnnouncement', announcement);

      const banner = data.find(banner => banner.type === 'education_banner' && banner.status);
      if (banner) commit('setEducationBanner', banner);
    } catch (error) {
      console.error('Error fetching education announcement and banner:', error);
    }
  },
};

const mutations = {
  setEducationTransaction(state, transaction) {
    const parseNumeric = value => {
      if (!value || value === '') return 0;
      const parsed = typeof value === 'string' ? parseInt(value, 10) : value;
      return isNaN(parsed) ? 0 : parsed;
    };

    state.transaction = {
      ...transaction,
      base_price: parseNumeric(transaction.base_price),
      total_price: parseNumeric(transaction.total_price),
      admin_fee: parseNumeric(transaction.admin_fee),
      payment_fee: parseNumeric(transaction.payment_fee),
      subsidi: parseNumeric(transaction.subsidi),
      amount: parseNumeric(transaction.amount),
      bill_count: parseNumeric(transaction.bill_count),
    };
  },
  setEducationTransactionLoading(state, isLoading) {
    state.loading = isLoading;
  },
  setCustomerNumber(state, customerNumber) {
    state.customerNumber = customerNumber;
  },
  setEducationAnnouncement(state, announcement) {
    state.educationAnnouncement = announcement;
  },
  setEducationBanner(state, banner) {
    state.educationBanner = banner;
  },
};

export default {
  state: () => ({ ...initialState }),
  mutations,
  getters,
  actions,
};
